import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useMessageModal } from "../admin/contexts/MessageModalContext";
import { useFormat } from "../context/FormatContext";
import DataTable, { type Column } from "../admin/components/DataTable";
import "../styles/marketPriceAnalysis.css";

type MarketResult = {
  id?: number;
  title?: string;
  sellerName?: string;
  seller_name?: string;
  domain?: string;
  url?: string;
  priceValue?: number;
  price_value?: number;
  currency?: string;
  rating?: number | null;
  reviewsCount?: number | null;
  reviews_count?: number | null;
  rankPosition?: number | null;
  rank_position?: number | null;
  matchScore?: number;
  match_score?: number;
  matchType?: "Exact" | "Similar" | "Weak" | "Unmatched";
  match_type?: "Exact" | "Similar" | "Weak" | "Unmatched";
};

type NormalizedMarketResult = {
  id?: number;
  title: string;
  sellerName: string;
  domain: string;
  url: string;
  priceValue: number | null;
  currency: string;
  rating: number | null;
  reviewsCount: number | null;
  rankPosition: number | null;
  matchScore: number;
  matchType: "Exact" | "Similar" | "Weak" | "Unmatched";
};

type MarketAnalysisResponse = {
  runId: number;
  searchKeyword: string;
  lowestPrice: number | null;
  averagePrice: number | null;
  highestPrice: number | null;
  resultCount: number;
  accessType: "Free" | "Paid";
  chargedAmount: number;
  wallet?: {
    balanceAmount: number;
    freeSearchUsed: boolean;
  };
  results: MarketResult[];
};

type WalletTransaction = {
  id: number;
  transaction_type: "Credit" | "Debit" | "FreeSearch";
  amount: number;
  reference_type?: string | null;
  reference_id?: string | null;
  note?: string | null;
  created_at: string;

  search_keyword?: string | null;
  access_type?: "Free" | "Paid" | null;
  analysis_status?: string | null;
  result_count?: number | null;
};

type WalletTopup = {
  id: number;
  payment_ref: string;
  plan_id: number;
  amount: number;
  title?: string | null;
  description?: string | null;
  payment_method?: string | null;
  status: "Pending" | "Success" | "Failed" | "Cancelled";
  securepay_transaction_id?: string | null;
  wallet_credited: number;
  created_at: string;
  updated_at: string;
};

type WalletHistoryData = {
  wallet: {
    balanceAmount: number;
    freeSearchUsed: boolean;
  };
  transactions: WalletTransaction[];
  topups: WalletTopup[];
};

const API_BASE_URL =
  import.meta.env.VITE_LYRAZONE_BASE_URL || "http://localhost:5000";

const getStoredSubscriberUser = () => {
  try {
    const storedSubscriberRaw = localStorage.getItem("subscriber_user");

    if (!storedSubscriberRaw) return null;

    const subscriberUser = JSON.parse(storedSubscriberRaw);

    if (!subscriberUser?.id) return null;

    if (
      subscriberUser?.tokenExpiry &&
      Number(subscriberUser.tokenExpiry) < Date.now()
    ) {
      localStorage.removeItem("subscriber_user");
      return null;
    }

    return subscriberUser;
  } catch (error) {
    console.error("Invalid subscriber_user in localStorage:", error);
    localStorage.removeItem("subscriber_user");
    return null;
  }
};

const MarketPriceAnalysis: React.FC = () => {
  const navigate = useNavigate();

  const { showModal } = useMessageModal();
  const { formatCurrency, formatDateToDMY } = useFormat();

  const [storedUser, setStoredUser] = useState<any>(() =>
    getStoredSubscriberUser(),
  );

  const [searchKeyword, setSearchKeyword] = useState("");
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState<MarketAnalysisResponse | null>(null);
  const [error, setError] = useState("");
  const [accessMessage, setAccessMessage] = useState("");
  const [walletBalance, setWalletBalance] = useState<number | null>(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletModalOpen, setWalletModalOpen] = useState(false);
  const [walletActiveTab, setWalletActiveTab] = useState<
    "transactions" | "topups"
  >("transactions");
  const [walletHistory, setWalletHistory] = useState<WalletHistoryData | null>(
    null,
  );
  const [walletHistoryLoading, setWalletHistoryLoading] = useState(false);
  const [walletHistoryError, setWalletHistoryError] = useState("");

  useEffect(() => {
    const refreshStoredUser = () => {
      setStoredUser(getStoredSubscriberUser());
    };

    refreshStoredUser();

    window.addEventListener("focus", refreshStoredUser);
    window.addEventListener("storage", refreshStoredUser);

    return () => {
      window.removeEventListener("focus", refreshStoredUser);
      window.removeEventListener("storage", refreshStoredUser);
    };
  }, []);

  useEffect(() => {
    fetchSubscriberWallet();

    const refreshWalletOnFocus = () => {
      fetchSubscriberWallet();
    };

    window.addEventListener("focus", refreshWalletOnFocus);

    return () => {
      window.removeEventListener("focus", refreshWalletOnFocus);
    };
  }, []);

  const handleWalletTopUp = () => {
    setWalletModalOpen(false);
    navigate("/market-analysis-pricing");
  };

  const fetchWalletHistory = async () => {
    const latestStoredUser = getStoredSubscriberUser();

    setStoredUser(latestStoredUser);

    if (!latestStoredUser) {
      showModal({
        type: "warning",
        title: "Warning",
        message: "You are not logged in, please login first.",
      });
      return;
    }

    try {
      setWalletHistoryLoading(true);
      setWalletHistoryError("");

      const response = await axios.get(
        `${API_BASE_URL}/market-analysis/wallet/history`,
        {
          withCredentials: true,
        },
      );

      if (response.data?.success) {
        setWalletHistory(response.data.data);

        if (response.data.data?.wallet) {
          setWalletBalance(
            Number(response.data.data.wallet.balanceAmount || 0),
          );
        }
      } else {
        setWalletHistoryError(
          response.data?.message || "Unable to fetch wallet history.",
        );
      }
    } catch (error: any) {
      setWalletHistoryError(
        error?.response?.data?.message ||
          error.message ||
          "Unable to fetch wallet history.",
      );
    } finally {
      setWalletHistoryLoading(false);
    }
  };

  const handleOpenWalletModal = async () => {
    setWalletModalOpen(true);
    setWalletActiveTab("transactions");
    await fetchWalletHistory();
  };

  const fetchSubscriberWallet = async () => {
    const latestStoredUser = getStoredSubscriberUser();

    setStoredUser(latestStoredUser);

    if (!latestStoredUser) {
      setWalletBalance(null);
      return;
    }

    try {
      setWalletLoading(true);

      const response = await axios.get(
        `${API_BASE_URL}/market-analysis/wallet`,
        {
          withCredentials: true,
        },
      );

      if (response.data?.success) {
        setWalletBalance(Number(response.data.data?.balanceAmount || 0));
      }
    } catch (error) {
      console.error("Fetch subscriber wallet error:", error);
      setWalletBalance(null);
    } finally {
      setWalletLoading(false);
    }
  };

  const normalizeMarketResults = (
    results: MarketResult[] = [],
  ): NormalizedMarketResult[] => {
    return results.map((item) => ({
      id: item.id,
      title: item.title || "-",
      sellerName: item.sellerName || item.seller_name || "-",
      domain: item.domain || "-",
      url: item.url || "",
      priceValue:
        item.priceValue !== undefined && item.priceValue !== null
          ? Number(item.priceValue)
          : item.price_value !== undefined && item.price_value !== null
            ? Number(item.price_value)
            : null,
      currency: item.currency || "MYR",
      rating:
        item.rating !== undefined && item.rating !== null
          ? Number(item.rating)
          : null,
      reviewsCount:
        item.reviewsCount !== undefined && item.reviewsCount !== null
          ? Number(item.reviewsCount)
          : item.reviews_count !== undefined && item.reviews_count !== null
            ? Number(item.reviews_count)
            : null,
      rankPosition:
        item.rankPosition !== undefined && item.rankPosition !== null
          ? Number(item.rankPosition)
          : item.rank_position !== undefined && item.rank_position !== null
            ? Number(item.rank_position)
            : null,
      matchScore:
        item.matchScore !== undefined && item.matchScore !== null
          ? Number(item.matchScore)
          : item.match_score !== undefined && item.match_score !== null
            ? Number(item.match_score)
            : 0,
      matchType: item.matchType || item.match_type || "Unmatched",
    }));
  };

  const marketResultRows = useMemo(() => {
    return normalizeMarketResults(analysis?.results || []);
  }, [analysis]);

  const marketResultColumns: Column[] = useMemo(
    () => [
      {
        key: "rowNo",
        label: "#",
        filterable: false,
        render: (_row, rowIndex) => rowIndex + 1,
      },
      {
        key: "title",
        label: "Market Product",
      },
      {
        key: "sellerName",
        label: "Seller",
      },
      {
        key: "priceValue",
        label: "Price",
        render: (row: NormalizedMarketResult) => formatCurrency(row.priceValue),
      },
      {
        key: "matchType",
        label: "Match",
        render: (row: NormalizedMarketResult) => (
          <span
            className={`market-analysis-badge ${String(
              row.matchType || "Unmatched",
            ).toLowerCase()}`}
          >
            {row.matchType || "Unmatched"}
          </span>
        ),
      },
      {
        key: "matchScore",
        label: "Score",
        render: (row: NormalizedMarketResult) => `${row.matchScore || 0}%`,
      },
      {
        key: "rating",
        label: "Rating",
        render: (row: NormalizedMarketResult) =>
          row.rating ? `${row.rating} (${row.reviewsCount || 0})` : "-",
      },
      {
        key: "domain",
        label: "Source",
        render: (row: NormalizedMarketResult) =>
          row.url ? (
            <a
              href={row.url}
              target="_blank"
              rel="noreferrer"
              className="market-analysis-source-link"
              onClick={(event) => event.stopPropagation()}
            >
              View
            </a>
          ) : (
            row.domain || "-"
          ),
      },
    ],
    [],
  );

  const handleAnalyze = async () => {
    if (!storedUser) {
      showModal({
        type: "warning",
        title: "Warning",
        message: "You are not logged in, please login first.",
      });
      return;
    }

    if (!searchKeyword.trim()) {
      setError("Please enter a product keyword.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setAccessMessage("");
      setAnalysis(null);

      const response = await axios.post(
        `${API_BASE_URL}/market-analysis/analyze`,
        {
          searchKeyword: searchKeyword.trim(),
        },
        {
          withCredentials: true,
        },
      );

      if (response.data?.success) {
        setAnalysis(response.data.data);

        if (response.data.data?.wallet) {
          setWalletBalance(
            Number(response.data.data.wallet.balanceAmount || 0),
          );
        }
      } else {
        setError(response.data?.message || "Market analysis failed.");
      }
    } catch (err: any) {
      const responseData = err?.response?.data;

      if (err?.response?.status === 401 || responseData?.requiresAuth) {
        showModal({
          type: "warning",
          title: "Warning",
          message: "You are not logged in, please login first.",
        });
        return;
      }

      if (err?.response?.status === 402 || responseData?.requiresAccess) {
        setAccessMessage(
          responseData?.message ||
            "Please get access for more Market Analysis, click here.",
        );
        return;
      }

      setError(
        responseData?.error ||
          responseData?.message ||
          err.message ||
          "Something went wrong.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="market-analysis-page">
      <section className="market-analysis-hero">
        <div className="market-analysis-hero-inner">
          <div className="market-analysis-hero-content">
            <span className="market-analysis-eyebrow">LyraZone Solution</span>

            <h1>Market Price Analysis</h1>

            <p>
              Search Malaysia market pricing by product keyword and compare
              current online price ranges before making your purchase or pricing
              decision.
            </p>
          </div>

          {storedUser && (
            <button
              type="button"
              className="market-analysis-wallet-card"
              onClick={handleOpenWalletModal}
            >
              <span>Available Balance</span>

              <strong>
                {walletLoading
                  ? "Loading..."
                  : formatCurrency(walletBalance || 0)}
              </strong>

              <small>Market Analysis Wallet</small>
            </button>
          )}
        </div>
      </section>

      <section className="market-analysis-content">
        <div className="market-analysis-search-card">
          <div className="market-analysis-search-title">
            <h2>Search Market Price</h2>
            <p>
              Enter a product name, model, barcode, or keyword to check
              available Malaysia market prices.
            </p>
          </div>

          <div className="market-analysis-search-grid">
            <div className="market-analysis-field">
              <label>Search Product Keyword</label>

              <input
                value={searchKeyword}
                onChange={(e) => {
                  setSearchKeyword(e.target.value);
                  setAccessMessage("");
                  setError("");
                }}
                placeholder="Example: TSC TX610 600dpi Barcode Printer Malaysia"
              />

              {accessMessage && (
                <div className="market-analysis-access-message">
                  Please get access for more Market Analysis,{" "}
                  <button
                    type="button"
                    onClick={() => navigate("/market-analysis-pricing")}
                  >
                    click here
                  </button>
                  .
                </div>
              )}
            </div>

            <button
              className="market-analysis-btn"
              onClick={handleAnalyze}
              disabled={loading}
            >
              {loading ? "Analyzing..." : "Analyze Market Price"}
            </button>
          </div>

          {error && <div className="market-analysis-error">{error}</div>}
        </div>

        {analysis && (
          <>
            <div className="market-analysis-summary">
              <div className="market-analysis-card">
                <span>Lowest Market Price</span>
                <strong>{formatCurrency(analysis.lowestPrice)}</strong>
                <small>Based on market results</small>
              </div>

              <div className="market-analysis-card">
                <span>Average Market Price</span>
                <strong>{formatCurrency(analysis.averagePrice)}</strong>
                <small>Malaysia market average</small>
              </div>

              <div className="market-analysis-card">
                <span>Highest Market Price</span>
                <strong>{formatCurrency(analysis.highestPrice)}</strong>
                <small>Based on market results</small>
              </div>

              <div className="market-analysis-card">
                <span>Results Found</span>
                <strong>{analysis.resultCount}</strong>
                <small>
                  {analysis.accessType === "Free"
                    ? "Free search used"
                    : `Charged ${formatCurrency(analysis.chargedAmount)}`}
                </small>
              </div>
            </div>

            <div className="market-analysis-summary-box">
              <h3>Market Search Summary</h3>

              <p>
                <b>Search Keyword:</b> {analysis.searchKeyword}
              </p>

              {analysis.wallet && (
                <p>
                  <b>Available Balance:</b>{" "}
                  {formatCurrency(analysis.wallet.balanceAmount)}
                </p>
              )}
            </div>

            <div className="market-analysis-results-section">
              <div className="market-analysis-results-header">
                <h3>Market Price Results</h3>
                <span>{marketResultRows.length} Result(s)</span>
              </div>

              <div className="market-analysis-desktop-table">
                <DataTable
                  columns={marketResultColumns}
                  data={marketResultRows}
                  getRowKey={(row, index) =>
                    row.id || `${row.title}-${row.sellerName}-${index}`
                  }
                />
              </div>

              <div className="market-analysis-mobile-cards">
                {marketResultRows.length === 0 ? (
                  <div className="market-analysis-mobile-empty">
                    No market price results found.
                  </div>
                ) : (
                  marketResultRows.map((row, index) => (
                    <div
                      className="market-analysis-mobile-card"
                      key={row.id || `${row.title}-${row.sellerName}-${index}`}
                    >
                      <div className="market-analysis-mobile-status">
                        <span
                          className={`market-analysis-mobile-status-badge ${String(
                            row.matchType || "Unmatched",
                          ).toLowerCase()}`}
                        >
                          {row.matchType || "Unmatched"}
                        </span>
                      </div>

                      <div className="market-analysis-mobile-field">
                        <span>Market Product</span>
                        <strong>{row.title || "-"}</strong>
                      </div>

                      <div className="market-analysis-mobile-field">
                        <span>Seller</span>
                        <strong>{row.sellerName || "-"}</strong>
                      </div>

                      <div className="market-analysis-mobile-info-row">
                        <div className="market-analysis-mobile-info-item">
                          <span>Price</span>
                          <strong>{formatCurrency(row.priceValue)}</strong>
                        </div>

                        <div className="market-analysis-mobile-info-item">
                          <span>Score</span>
                          <strong>{row.matchScore || 0}%</strong>
                        </div>

                        <div className="market-analysis-mobile-info-item">
                          <span>Rating</span>
                          <strong>
                            {row.rating
                              ? `${row.rating} (${row.reviewsCount || 0})`
                              : "-"}
                          </strong>
                        </div>
                      </div>

                      {row.url ? (
                        <a
                          href={row.url}
                          target="_blank"
                          rel="noreferrer"
                          className="market-analysis-mobile-view-btn"
                          onClick={(event) => event.stopPropagation()}
                        >
                          View Source
                        </a>
                      ) : (
                        <button
                          type="button"
                          className="market-analysis-mobile-view-btn disabled"
                          disabled
                        >
                          No Source
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </>
        )}
      </section>
      {walletModalOpen && (
        <div
          className="market-wallet-modal-overlay"
          onClick={() => setWalletModalOpen(false)}
        >
          <div
            className="market-wallet-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="market-wallet-modal-header">
              <div className="market-wallet-modal-balance">
                <span>Market Analysis Wallet</span>
                <h3>
                  {formatCurrency(
                    walletHistory?.wallet?.balanceAmount ?? walletBalance ?? 0,
                  )}
                </h3>
                <p>Available Balance</p>
              </div>

              <div className="market-wallet-modal-actions">
                <button
                  type="button"
                  className="market-wallet-topup-btn"
                  onClick={handleWalletTopUp}
                >
                  Top Up Balance
                </button>

                <button
                  type="button"
                  className="market-wallet-modal-close"
                  onClick={() => setWalletModalOpen(false)}
                >
                  ×
                </button>
              </div>
            </div>

            <div className="market-wallet-tabs">
              <button
                type="button"
                className={walletActiveTab === "transactions" ? "active" : ""}
                onClick={() => setWalletActiveTab("transactions")}
              >
                Transaction History
              </button>

              <button
                type="button"
                className={walletActiveTab === "topups" ? "active" : ""}
                onClick={() => setWalletActiveTab("topups")}
              >
                Top-up History
              </button>
            </div>

            <div className="market-wallet-modal-body">
              {walletHistoryLoading ? (
                <div className="market-wallet-empty">
                  Loading wallet history...
                </div>
              ) : walletHistoryError ? (
                <div className="market-wallet-error">{walletHistoryError}</div>
              ) : walletActiveTab === "transactions" ? (
                walletHistory?.transactions?.length ? (
                  <div className="market-wallet-list">
                    {walletHistory.transactions.map((item) => (
                      <div className="market-wallet-row" key={item.id}>
                        <div>
                          <strong>{item.transaction_type}</strong>

                          {item.search_keyword ? (
                            <>
                              <span>Search: {item.search_keyword}</span>
                              <small>
                                {item.note || "-"} •{" "}
                                {formatDateToDMY(item.created_at)}
                              </small>
                            </>
                          ) : (
                            <>
                              <span>
                                {item.note || item.reference_type || "-"}
                              </span>
                              <small>{formatDateToDMY(item.created_at)}</small>
                            </>
                          )}
                        </div>

                        <b
                          className={
                            item.transaction_type === "Debit"
                              ? "debit"
                              : item.transaction_type === "FreeSearch"
                                ? "free"
                                : "credit"
                          }
                        >
                          {item.transaction_type === "Debit" ? "-" : "+"}
                          {formatCurrency(Number(item.amount || 0))}
                        </b>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="market-wallet-empty">
                    No transaction history found.
                  </div>
                )
              ) : walletHistory?.topups?.length ? (
                <div className="market-wallet-list">
                  {walletHistory.topups.map((item) => (
                    <div className="market-wallet-row" key={item.id}>
                      <div>
                        <strong>
                          {item.title || "Market Analysis Top-up"}
                        </strong>
                        <span>Ref: {item.payment_ref}</span>
                        <small>{formatDateToDMY(item.created_at)}</small>
                      </div>

                      <div className="market-wallet-topup-right">
                        <b>{formatDateToDMY(Number(item.amount || 0))}</b>
                        <em className={String(item.status || "").toLowerCase()}>
                          {item.status}
                        </em>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="market-wallet-empty">
                  No top-up history found.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketPriceAnalysis;
